import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import api from "../utils/api";
import ReviewsBar from "../components/ReviewsBar";
import LiveStrip from "../components/LiveStrip";
import "../styles/parkingVsTaxi.css";

const PARKS = {
  legoland: {
    key: "legoland",
    name: "LEGOLAND Windsor",
    shortName: "LEGOLAND",
    dest: "SL4 4AY",
    coords: { lat: 51.462807, lon: -0.647736 },
    distanceEndpoint: "/api/legoland/distance",
    dayEndpoint: "/api/legoland/day",
    monthEndpoint: "/api/legoland/month",
    railEndpoint: "/api/legoland-rail/plan",
    freeHeightLabel: "Under 90cm",
    bestFor: "Younger kids",
    thrill: "Family / gentle",
    walkMins: 9,
    parking: { standard: 12, priority: 18 },
    accent: {
      hero: "linear-gradient(135deg, #0f3ea3 0%, #1d67d8 55%, #35b7ff 100%)",
      border: "#3b82f6",
      soft: "linear-gradient(180deg, #f7fbff 0%, #eef6ff 100%)",
      chip: "#dbeafe",
      chipText: "#1d4ed8",
    },
  },
  thorpe: {
    key: "thorpe",
    name: "Thorpe Park",
    shortName: "Thorpe",
    dest: "KT16 8PN",
    coords: { lat: 51.405991, lon: -0.513151 },
    distanceEndpoint: "/api/thorpepark/distance",
    dayEndpoint: "/api/thorpepark/day",
    monthEndpoint: "/api/thorpepark/month",
    railEndpoint: "/api/thorpe-rail/plan",
    freeHeightLabel: "Under 1.2m",
    bestFor: "Teens & thrills",
    thrill: "Thrill / extreme",
    walkMins: 6,
    parking: { standard: 12, priority: 20 },
    accent: {
      hero: "linear-gradient(135deg, #17412f 0%, #0f766e 50%, #22c55e 100%)",
      border: "#10b981",
      soft: "linear-gradient(180deg, #f7fffb 0%, #ecfdf5 100%)",
      chip: "#d1fae5",
      chipText: "#047857",
    },
  },
};

const PARK_KEYS = ["legoland", "thorpe"];

const defaultDate = () => {
  const now = new Date();
  const target = new Date(now);
  target.setDate(now.getDate() + 30);
  const pad = (n) => String(n).padStart(2, "0");
  return `${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}`;
};

const clamp = (v, min, max, fallback) => {
  const n = Number(v);
  if (Number.isNaN(n)) return fallback;
  return Math.min(max, Math.max(min, n));
};

const isFullPostcode = (pc) => /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(String(pc || "").trim());

const toDateOnly = (value) => {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return String(value);
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formatDisplayDate = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value || "";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const buildDepartAt = (date) => {
  const dateOnly = toDateOnly(date);
  return dateOnly ? `${dateOnly}T09:30` : null;
};

const buildWeatherTargetLocal = (dateStr, openTime) => {
  if (!dateStr || !openTime) return null;
  return `${dateStr}T${String(openTime).slice(0, 5)}`;
};

const toSafeTaxiPreviewTime = (value, minHoursAhead = 5) => {
  const selected = value ? new Date(value) : null;
  const minFuture = new Date(Date.now() + minHoursAhead * 60 * 60 * 1000);
  if (!(selected instanceof Date) || Number.isNaN(selected.getTime())) return minFuture.toISOString();
  return (selected.getTime() < minFuture.getTime() ? minFuture : selected).toISOString();
};

const toSafeRailLiveTime = (value, minMinutesAhead = 90) => {
  const selected = value ? new Date(value) : null;
  const minFuture = new Date(Date.now() + minMinutesAhead * 60 * 1000);
  if (!(selected instanceof Date) || Number.isNaN(selected.getTime())) return null;
  return selected.getTime() < minFuture.getTime() ? null : selected.toISOString();
};

const money = (v, decimals = 0) => (v == null || Number.isNaN(Number(v)) ? "—" : `£${Number(v).toFixed(decimals)}`);

const rangeText = (min, max) => {
  if (min == null && max == null) return "—";
  if (min === max) return money(min);
  return `${money(min)} - ${money(max)}`;
};

const formatTime = (t) => (!t ? "—" : typeof t === "string" && t.length >= 5 ? t.slice(0, 5) : t);

const changeCountText = (count) => `${count} ${count === 1 ? "change" : "changes"}`;

const stationLabelFromCrs = (crs, fallback = "origin station") => {
  const code = String(crs || "").toUpperCase();
  if (code === "WAT") return "Waterloo";
  if (code === "VIC") return "Victoria";
  if (code === "PAD") return "Paddington";
  if (code === "CHX") return "Charing Cross";
  if (code === "CLJ") return "Clapham Junction";
  if (code === "SNS") return "Staines";
  if (code === "CHY") return "Chertsey";
  if (code === "SLO") return "Slough";
  if (code === "WNR") return "Windsor & Eton Riverside";
  return crs || fallback;
};

const taxiSpeedMphForDistance = (dist) => {
  if (dist <= 15) return 30;
  if (dist <= 60) return 42;
  return 52;
};

const baseFuel = (distanceMiles, legs = 2) => Math.round(distanceMiles * legs * 0.23);
const modelledTaxiCost = (distanceMiles, legs = 2) => Math.round((18 + 2.05 * distanceMiles) * legs);
const driveTravelTime = (distanceMiles) => Math.round((distanceMiles / 40) * 60 + 12);

const modelledRailFareAdult = (dist) => {
  if (dist == null) return null;
  if (dist <= 10) return 4;
  if (dist <= 30) return 18;
  if (dist <= 60) return 40;
  if (dist <= 120) return 80;
  if (dist <= 200) return 120;
  return 170;
};

function useParkData(selectedDate) {
  const [data, setData] = useState({
    legoland: { day: null, dayStatus: null, dayError: null },
    thorpe: { day: null, dayStatus: null, dayError: null },
  });

  useEffect(() => {
    if (!selectedDate) return;
    let cancelled = false;

    const fetchPark = async (key) => {
      const endpoint = PARKS[key].dayEndpoint;
      try {
        const res = await api.get(endpoint, { params: { date: selectedDate } });
        if (cancelled) return;
        setData((prev) => ({
          ...prev,
          [key]: { day: res.data, dayStatus: res.status, dayError: null },
        }));
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        const fallbackClosed = { is_open: false, open_time: null, close_time: null, price_from_gbp: null };
        setData((prev) => ({
          ...prev,
          [key]: {
            day: fallbackClosed,
            dayStatus: status || "error",
            dayError: status === 404 ? "No data for this date" : "Live park data unavailable",
          },
        }));
      }
    };

    PARK_KEYS.forEach((key) => {
      fetchPark(key);
    });

    return () => {
      cancelled = true;
    };
  }, [selectedDate]);

  return data;
}

export default function LegolandVsThorpePark() {
  const compactInput = { fontSize: "14px", padding: "8px 10px", height: "40px" };
  const compactSelect = { ...compactInput };
  const isMobile = typeof window !== "undefined" ? window.innerWidth <= 640 : false;
  const mobileCompactInput = isMobile ? { fontSize: "13px", padding: "7px 9px", height: "38px" } : compactInput;
  const mobileCompactSelect = isMobile ? { ...mobileCompactInput } : compactSelect;
  const labelStyle = {
    fontSize: isMobile ? "11px" : "13px",
    fontWeight: 700,
    minHeight: isMobile ? "24px" : "32px",
    lineHeight: isMobile ? 1.15 : undefined,
    display: "flex",
    alignItems: "center",
  };

  const initialTrip = {
    pickup: "SW1A 1AA",
    date: "",
    adults: 2,
    kidsTall: 1,
    kidsSmall: 0,
    preference: "compare",
  };
  const [formTrip, setFormTrip] = useState(initialTrip);
  const [appliedTrip, setAppliedTrip] = useState(initialTrip);
  const trip = appliedTrip;

  const [refreshFlash, setRefreshFlash] = useState(false);
  const [lastChangedField, setLastChangedField] = useState(null);
  const refreshTimer = useRef(null);
  const [initialDateSet, setInitialDateSet] = useState(false);
  const [distance, setDistance] = useState({
    legoland: null,
    thorpe: null,
    loading: false,
    error: null,
  });
  const [postcodeError, setPostcodeError] = useState(null);
  const [postcodeSuggestions, setPostcodeSuggestions] = useState([]);
  const [postcodeLookupLoading, setPostcodeLookupLoading] = useState(false);
  const [validatedPickup, setValidatedPickup] = useState(null);
  const [showPostcodeSuggestions, setShowPostcodeSuggestions] = useState(false);
  const [liveTransport, setLiveTransport] = useState({
    loading: false,
    error: null,
    legoland: { taxi: null, rail: null },
    thorpe: { taxi: null, rail: null },
  });
  const [parkWeather, setParkWeather] = useState({ legoland: null, thorpe: null });

  useEffect(() => () => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
  }, []);

  const triggerUpdateFlash = (field) => {
    setLastChangedField(field);
    setRefreshFlash(true);
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(() => setRefreshFlash(false), 900);
  };

  useEffect(() => {
    if (initialDateSet || formTrip.date) return;
    let cancelled = false;

    const today = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const monthKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
    const todayDateOnly = toDateOnly(today);

    const findNextOpen = async () => {
      let earliestBothOpen = null;
      let earliestAnyOpen = null;
      try {
        let probe = new Date(today);
        for (let i = 0; i < 12; i += 1) {
          const month = monthKey(probe);
          const [lego, thorpe] = await Promise.all([
            api.get(PARKS.legoland.monthEndpoint, { params: { month } }),
            api.get(PARKS.thorpe.monthEndpoint, { params: { month } }),
          ]);

          const legoOpenDates = new Set(
            (lego.data?.days || [])
              .filter((d) => d?.is_open)
              .map((d) => toDateOnly(d.visit_date))
              .filter(Boolean)
              .filter((date) => date > todayDateOnly)
          );
          const thorpeOpenDates = new Set(
            (thorpe.data?.days || [])
              .filter((d) => d?.is_open)
              .map((d) => toDateOnly(d.visit_date))
              .filter(Boolean)
              .filter((date) => date > todayDateOnly)
          );

          const bothOpenDates = [...legoOpenDates]
            .filter((date) => thorpeOpenDates.has(date))
            .sort();

          const anyOpenDates = [...new Set([...legoOpenDates, ...thorpeOpenDates])].sort();

          if (!earliestAnyOpen && anyOpenDates.length) {
            earliestAnyOpen = anyOpenDates[0];
          }

          if (bothOpenDates.length) {
            earliestBothOpen = bothOpenDates[0];
            break;
          }

          probe.setMonth(probe.getMonth() + 1);
        }
      } catch (err) {
      } finally {
        if (!cancelled) {
          const resolvedDate = earliestBothOpen || earliestAnyOpen || defaultDate();
          setFormTrip((prev) => ({ ...prev, date: prev.date || resolvedDate }));
          setAppliedTrip((prev) => ({ ...prev, date: prev.date || resolvedDate }));
          setInitialDateSet(true);
        }
      }
    };

    findNextOpen();

    return () => {
      cancelled = true;
    };
  }, [formTrip.date, initialDateSet]);

  useEffect(() => {
    const query = String(formTrip.pickup || "").toUpperCase().replace(/\s+/g, "");
    if (query.length < 3) {
      setPostcodeSuggestions([]);
      setShowPostcodeSuggestions(false);
      return;
    }

    const controller = new AbortController();
    const handle = setTimeout(async () => {
      try {
        const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(query)}/autocomplete?limit=6`, {
          signal: controller.signal,
        });
        const data = await res.json();
        if (!controller.signal.aborted) {
          const next = Array.isArray(data?.result) ? data.result : [];
          setPostcodeSuggestions(next);
          setShowPostcodeSuggestions(next.length > 0);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          setPostcodeSuggestions([]);
          setShowPostcodeSuggestions(false);
        }
      }
    }, 180);

    return () => {
      controller.abort();
      clearTimeout(handle);
    };
  }, [formTrip.pickup]);

  useEffect(() => {
    const candidate = String(formTrip.pickup || "").toUpperCase().replace(/\s+/g, " ").trim();
    if (!candidate) {
      setValidatedPickup(null);
      setPostcodeError(null);
      setPostcodeLookupLoading(false);
      return;
    }
    if (!isFullPostcode(candidate)) {
      setValidatedPickup(null);
      setPostcodeError(null);
      setPostcodeLookupLoading(false);
      return;
    }

    let cancelled = false;
    const handle = setTimeout(async () => {
      setPostcodeLookupLoading(true);
      try {
        const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(candidate.replace(/\s/g, ""))}`);
        const data = await res.json();
        if (cancelled) return;

        if (res.ok && data?.result?.postcode) {
          const canonical = data.result.postcode;
          setValidatedPickup(canonical);
          setPostcodeError(null);
          setFormTrip((prev) => (prev.pickup === formTrip.pickup ? { ...prev, pickup: canonical } : prev));
        } else {
          setValidatedPickup(null);
          setPostcodeError("Enter a valid UK postcode.");
        }
      } catch (err) {
        if (!cancelled) {
          setValidatedPickup(null);
          setPostcodeError("Postcode check unavailable right now.");
        }
      } finally {
        if (!cancelled) setPostcodeLookupLoading(false);
      }
    }, 180);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [formTrip.pickup]);

  useEffect(() => {
    if (!isFullPostcode(trip.pickup)) {
      setDistance({
        legoland: null,
        thorpe: null,
        loading: false,
        error: "Enter a full UK postcode (for example SW1A 1AA).",
      });
      return;
    }

    let cancelled = false;
    setDistance((prev) => ({ ...prev, loading: true, error: null }));

    const fetchDistance = async (key) => {
      const res = await api.get(PARKS[key].distanceEndpoint, { params: { from: trip.pickup } });
      return res.data?.miles ?? null;
    };

    const go = async () => {
      try {
        const [legoMiles, thorpeMiles] = await Promise.all([
          fetchDistance("legoland"),
          fetchDistance("thorpe"),
        ]);
        if (cancelled) return;
        setDistance({
          legoland: legoMiles,
          thorpe: thorpeMiles,
          loading: false,
          error: null,
        });
      } catch (err) {
        if (cancelled) return;
        setDistance({
          legoland: null,
          thorpe: null,
          loading: false,
          error: "Distance lookup unavailable right now.",
        });
      }
    };

    go();

    return () => {
      cancelled = true;
    };
  }, [trip.pickup]);

  const selectedDate = toDateOnly(trip.date);
  const parkData = useParkData(selectedDate);

  useEffect(() => {
    if (!selectedDate) {
      setParkWeather({ legoland: null, thorpe: null });
      return;
    }

    let cancelled = false;

    const fetchWeather = async (parkKey) => {
      const openTime = parkData[parkKey]?.day?.open_time;
      const isOpen = parkData[parkKey]?.day?.is_open;
      const targetLocal = isOpen ? buildWeatherTargetLocal(selectedDate, openTime) : null;
      if (!targetLocal) return [parkKey, null];

      try {
        const res = await api.get("/api/open/weather/coords", {
          params: {
            lat: PARKS[parkKey].coords.lat,
            lon: PARKS[parkKey].coords.lon,
            targetLocal,
          },
        });
        return [parkKey, res.data?.summary || null];
      } catch (err) {
        return [parkKey, null];
      }
    };

    const run = async () => {
      const entries = await Promise.all(PARK_KEYS.map((parkKey) => fetchWeather(parkKey)));
      if (cancelled) return;
      setParkWeather(Object.fromEntries(entries));
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [parkData, selectedDate]);

  const family = useMemo(() => {
    const adults = clamp(trip.adults, 0, 8, 2);
    const kidsTall = clamp(trip.kidsTall, 0, 8, 1);
    const kidsSmall = clamp(trip.kidsSmall, 0, 8, 0);
    return {
      adults,
      kidsTall,
      kidsSmall,
      size: adults + kidsTall + kidsSmall,
      payingPassengers: adults + kidsTall,
    };
  }, [trip.adults, trip.kidsTall, trip.kidsSmall]);

  const hasPendingChanges = useMemo(() => {
    return JSON.stringify(formTrip) !== JSON.stringify(appliedTrip);
  }, [appliedTrip, formTrip]);

  const compareDisabled =
    postcodeLookupLoading ||
    (!!formTrip.pickup && isFullPostcode(formTrip.pickup) && !validatedPickup) ||
    !!postcodeError;

  const applyComparisonInputs = () => {
    const normalizedPickup = validatedPickup || String(formTrip.pickup || "").toUpperCase().replace(/\s+/g, " ").trim();
    if (!normalizedPickup || !validatedPickup) {
      setPostcodeError("Enter a valid UK postcode.");
      return;
    }
    setAppliedTrip({
      ...formTrip,
      pickup: normalizedPickup,
      adults: clamp(formTrip.adults, 0, 8, 2),
      kidsTall: clamp(formTrip.kidsTall, 0, 8, 1),
      kidsSmall: clamp(formTrip.kidsSmall, 0, 8, 0),
    });
    setFormTrip((prev) => ({ ...prev, pickup: normalizedPickup }));
  };

  const handlePostcodeSuggestionPick = (postcode) => {
    triggerUpdateFlash("pickup");
    setFormTrip((prev) => ({ ...prev, pickup: postcode }));
    setShowPostcodeSuggestions(false);
  };

  const visiblePostcodeSuggestions = useMemo(() => {
    const current = String(formTrip.pickup || "").toUpperCase().replace(/\s+/g, " ").trim();
    return postcodeSuggestions.filter((postcode) => String(postcode || "").toUpperCase().replace(/\s+/g, " ").trim() !== current);
  }, [formTrip.pickup, postcodeSuggestions]);

  useEffect(() => {
    const departAt = buildDepartAt(selectedDate);
    const railDepartAt = toSafeRailLiveTime(departAt);
    if (!departAt || !isFullPostcode(trip.pickup)) {
      setLiveTransport({
        loading: false,
        error: null,
        legoland: { taxi: null, rail: null },
        thorpe: { taxi: null, rail: null },
      });
      return;
    }

    let cancelled = false;
    setLiveTransport((prev) => ({ ...prev, loading: true, error: null }));

    const fetchTaxi = async (key) => {
      const res = await api.post("/api/quote-preview", {
        pickup: trip.pickup,
        dropoff: PARKS[key].dest,
        passengers: Math.max(1, family.size || 1),
        bags: Math.max(0, family.size || 0),
        pickup_time: toSafeTaxiPreviewTime(departAt),
      });

      const vehicles = Array.isArray(res.data?.vehicles) ? res.data.vehicles : [];
      const cheapest = vehicles
        .filter((v) => v?.price != null)
        .map((v) => ({ ...v, price: Number(v.price) }))
        .sort((a, b) => a.price - b.price)[0];

      return cheapest
        ? {
            vehicle: cheapest.code || null,
            oneWayFare: Number(cheapest.price),
            distanceKm: res.data?.distance_km != null ? Number(res.data.distance_km) : null,
            source: "quote-preview-v2",
          }
        : null;
    };

    const fetchRail = async (key) => {
      if (!railDepartAt) return null;
      const res = await api.post(PARKS[key].railEndpoint, {
        pickup_postcode: trip.pickup,
        depart_at: railDepartAt,
        adults: family.adults,
        children: family.kidsTall,
      });
      return res.data?.success ? res.data : null;
    };

    const run = async () => {
      try {
        const results = await Promise.all(
          PARK_KEYS.map(async (key) => {
            const [taxiRes, railRes] = await Promise.allSettled([fetchTaxi(key), fetchRail(key)]);
            return {
              key,
              taxi: taxiRes.status === "fulfilled" ? taxiRes.value : null,
              rail: railRes.status === "fulfilled" ? railRes.value : null,
            };
          })
        );

        if (cancelled) return;

        setLiveTransport({
          loading: false,
          error: null,
          legoland: results.find((item) => item.key === "legoland") || { taxi: null, rail: null },
          thorpe: results.find((item) => item.key === "thorpe") || { taxi: null, rail: null },
        });
      } catch (err) {
        if (cancelled) return;
        setLiveTransport({
          loading: false,
          error: "Live rail and taxi refresh failed. Showing modelled fallback where needed.",
          legoland: { taxi: null, rail: null },
          thorpe: { taxi: null, rail: null },
        });
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [selectedDate, trip.pickup, family.adults, family.kidsTall, family.size]);

  const foodEstimate = useMemo(() => {
    const heads = Math.max(1, family.size);
    return { min: 12 * heads, max: 24 * heads, mid: Math.round((12 * heads + 24 * heads) / 2) };
  }, [family.size]);

  const parkModels = useMemo(() => {
    const buildRailFallback = (key, miles) => {
      if (!miles) return null;
      const adults = family.adults;
      const kids = family.kidsTall;
      const baseAdult = modelledRailFareAdult(miles);
      if (baseAdult == null) return null;
      const modelAdult = miles >= 40 ? Math.max(Math.round(baseAdult * 0.9), baseAdult - 5) : baseAdult;
      const child = Math.round(modelAdult * 0.6);
      const railFare = Math.round(adults * modelAdult + kids * child);
      if (key === "thorpe") {
        const lastMileBus = Math.round((adults + kids) * 3.5);
        return {
          live: false,
          total: railFare + lastMileBus,
          duration: Math.round((miles / 23) * 60 + 38),
          walkMins: 0,
          changes: miles > 30 ? 2 : 1,
          originCrs: null,
          gatewayName: "Staines Rail Station",
          gatewayCrs: "SNS",
          accessMode: null,
          sourceMode: "modelled_fallback",
          lastMileLabel: "950 Thorpe Park Express",
          lastMileBusGbp: lastMileBus,
          railFare,
          firstMileLabel: `${trip.pickup} -> origin station by local`,
          lastMileInfo: "Staines -> Thorpe Park via 950",
          meta: `Train to Staines + express bus`,
          breakdown: `${money(railFare)} rail + ${money(lastMileBus)} bus`,
        };
      }
      const lastMileTaxi = 18;
        return {
          live: false,
          total: railFare + lastMileTaxi,
          duration: Math.round((miles / 24) * 60 + 36),
          walkMins: 0,
          changes: miles > 30 ? 2 : 1,
          originCrs: null,
          gatewayName: "Slough Rail Station",
          gatewayCrs: "SLO",
          accessMode: null,
          sourceMode: "modelled_fallback",
          lastMileTaxiGbp: lastMileTaxi,
          railFare,
          firstMileLabel: `${trip.pickup} -> origin station by local`,
          lastMileInfo: "Slough -> LEGOLAND by taxi",
          meta: `Train to Slough + short taxi`,
          breakdown: `${money(railFare)} rail + ${money(lastMileTaxi)} last-mile taxi`,
        };
    };

    return PARK_KEYS.reduce((acc, key) => {
      const miles = distance[key];
      const liveTaxi = liveTransport[key]?.taxi || null;
      const liveRail = liveTransport[key]?.rail?.data || null;
      const liveRailMeta = liveTransport[key]?.rail || null;
      const entryPrice = parkData[key].day?.price_from_gbp != null ? Number(parkData[key].day.price_from_gbp) : null;
      const entryTotal = entryPrice != null ? entryPrice * family.payingPassengers : null;

      const driveTransport = miles
        ? {
            total: baseFuel(miles, 2) + PARKS[key].parking.standard,
            duration: driveTravelTime(miles),
            walkMins: PARKS[key].walkMins,
            meta: `${PARKS[key].walkMins} min walk`,
            breakdown: `${money(baseFuel(miles, 2))} fuel + ${money(PARKS[key].parking.standard)} parking`,
          }
        : null;

      const taxiOneWayFare = liveTaxi?.oneWayFare != null ? Number(liveTaxi.oneWayFare) : null;
      const taxiTransport = miles
        ? (() => {
            const total = taxiOneWayFare != null ? Number((taxiOneWayFare * 2).toFixed(2)) : modelledTaxiCost(miles, 2);
            const taxiDistanceMiles =
              liveTaxi?.distanceKm != null ? Number(liveTaxi.distanceKm) * 0.621371 : miles;
            const duration = Math.round(
              (taxiDistanceMiles / taxiSpeedMphForDistance(taxiDistanceMiles)) * 60 + 14
            );
            return {
              live: taxiOneWayFare != null,
              total,
              duration,
              walkMins: 3,
              vehicle: liveTaxi?.vehicle || null,
              meta: `Door-to-gate with ~3 min walk`,
              breakdown: taxiOneWayFare != null
                ? `${money(total)} live taxi${liveTaxi?.vehicle ? ` (${liveTaxi.vehicle})` : ""}`
                : `${money(total)} modelled taxi`,
            };
          })()
        : null;

      const railTransport = (() => {
        if (liveRail?.total_public_transport_cost_gbp != null) {
          const walkMins = Number(liveRail.access_walk_mins || 0) + Number(liveRail.last_mile_walk_mins || 0);
          const changes = Number(liveRail.interchanges || 0);
          const total = Number(liveRail.total_public_transport_cost_gbp || 0);
          const duration = Number(liveRail.total_public_transport_duration_mins || 0) || null;
          const metaBits = [];
          if (liveRail.gateway_name) metaBits.push(liveRail.gateway_name);
          if (changes > 0) metaBits.push(changeCountText(changes));
          if (walkMins > 0) metaBits.push(`${walkMins} min walk`);
          const breakdownBits = [`${money(liveRail.rail_fare_gbp || 0)} rail`];
          if (liveRail.access_taxi_gbp) breakdownBits.push(`${money(liveRail.access_taxi_gbp)} station taxi`);
          if (liveRail.last_mile_taxi_gbp) breakdownBits.push(`${money(liveRail.last_mile_taxi_gbp)} last-mile taxi`);
          if (liveRail.last_mile_bus_gbp) breakdownBits.push(`${money(liveRail.last_mile_bus_gbp)} ${liveRail.last_mile_label || "bus"}`);

          return {
            live: liveRailMeta?.mode !== "modelled_fallback",
            total,
            duration,
            walkMins,
            changes,
            originCrs: liveRail.origin_selection?.preferred_crs || null,
            gatewayName: liveRail.gateway_name || null,
            gatewayCrs: liveRail.gateway_crs || null,
            accessMode: liveRail.access_mode || null,
            sourceMode: liveRailMeta?.mode || null,
            lastMileLabel: liveRail.last_mile_label || null,
            railFare: Number(liveRail.rail_fare_gbp || 0),
            firstMileLabel:
              liveRail.access_mode === "taxi"
                ? `${trip.pickup} -> ${stationLabelFromCrs(liveRail.origin_selection?.preferred_crs)} by taxi`
                : liveRail.access_mode === "walk"
                ? `${trip.pickup} -> ${stationLabelFromCrs(liveRail.origin_selection?.preferred_crs)} by walk`
                : `${trip.pickup} -> ${stationLabelFromCrs(liveRail.origin_selection?.preferred_crs)} by local`,
            lastMileInfo:
              key === "thorpe"
                ? `${stationLabelFromCrs(liveRail.gateway_crs, "Staines")} -> Thorpe Park via ${liveRail.last_mile_label?.includes("446") ? "446" : "950"}`
                : `${stationLabelFromCrs(liveRail.gateway_crs, "Slough")} -> LEGOLAND by taxi`,
            meta: metaBits.join(" • ") || "Rail itinerary",
            breakdown: breakdownBits.join(" + "),
          };
        }
        return buildRailFallback(key, miles);
      })();

      const transportModes = {
        drive: driveTransport
          ? {
              key: "drive",
              label: "Self drive",
              total: entryTotal != null ? entryTotal + driveTransport.total + foodEstimate.mid : null,
              rangeMin: entryTotal != null ? entryTotal + driveTransport.total + foodEstimate.min : null,
              rangeMax: entryTotal != null ? entryTotal + driveTransport.total + foodEstimate.max : null,
              duration: driveTransport.duration,
              walkMins: driveTransport.walkMins,
              liveBadge: null,
              meta: driveTransport.meta,
              breakdown: `${entryTotal != null ? `${money(entryTotal)} tickets` : "Tickets unavailable"} + ${driveTransport.breakdown} + ${rangeText(foodEstimate.min, foodEstimate.max)} food`,
              transportCost: driveTransport.total,
              fareLabel: `${money(driveTransport.total)} fuel + parking`,
              walkChangesLabel: `${driveTransport.walkMins} min walk • 0 changes`,
              hassleLabel: driveTransport.walkMins >= 8 ? "Medium hassle" : "Low hassle",
              additionalInfo: "Most control over arrival and return time",
            }
          : null,
        public: railTransport
          ? {
              key: "public",
              label: "Train",
              total: entryTotal != null ? entryTotal + railTransport.total + foodEstimate.mid : null,
              rangeMin: entryTotal != null ? entryTotal + railTransport.total + foodEstimate.min : null,
              rangeMax: entryTotal != null ? entryTotal + railTransport.total + foodEstimate.max : null,
              duration: railTransport.duration,
              walkMins: railTransport.walkMins,
              changes: railTransport.changes,
              liveBadge: railTransport.live ? "Live rail" : "Fallback",
              meta: railTransport.meta,
              breakdown: `${entryTotal != null ? `${money(entryTotal)} tickets` : "Tickets unavailable"} + ${railTransport.breakdown} + ${rangeText(foodEstimate.min, foodEstimate.max)} food`,
              transportCost: railTransport.total,
              fareLabel: railTransport.railFare != null ? `${money(railTransport.railFare)} rail fare` : money(railTransport.total),
              walkChangesLabel: `${railTransport.walkMins ?? 0} min walk • ${changeCountText(railTransport.changes ?? 0)}`,
              hassleLabel: (railTransport.walkMins ?? 0) >= 10 || (railTransport.changes ?? 0) >= 2 ? "Higher hassle" : "Medium hassle",
              additionalInfo: `${railTransport.firstMileLabel} • ${railTransport.lastMileInfo}`,
            }
          : null,
        taxi: taxiTransport
          ? {
              key: "taxi",
              label: "Taxi",
              total: entryTotal != null ? entryTotal + taxiTransport.total + foodEstimate.mid : null,
              rangeMin: entryTotal != null ? entryTotal + taxiTransport.total + foodEstimate.min : null,
              rangeMax: entryTotal != null ? entryTotal + taxiTransport.total + foodEstimate.max : null,
              duration: taxiTransport.duration,
              walkMins: taxiTransport.walkMins,
              liveBadge: taxiTransport.live ? "Live taxi" : null,
              meta: taxiTransport.meta,
              breakdown: `${entryTotal != null ? `${money(entryTotal)} tickets` : "Tickets unavailable"} + ${taxiTransport.breakdown} + ${rangeText(foodEstimate.min, foodEstimate.max)} food`,
              transportCost: taxiTransport.total,
              fareLabel: `${money(taxiTransport.total)} taxi fare`,
              walkChangesLabel: `${taxiTransport.walkMins} min walk • 0 changes`,
              hassleLabel: "Low hassle",
              additionalInfo: taxiTransport.live ? "Door-to-gate quote from live taxi data" : "Modelled door-to-gate taxi estimate",
            }
          : null,
      };

      const availableModes = Object.values(transportModes).filter(Boolean);
      const sortedAvailableModes = [...availableModes].sort(
        (a, b) => (a.total ?? Number.POSITIVE_INFINITY) - (b.total ?? Number.POSITIVE_INFINITY)
      );
      const compareModeKey = trip.preference === "compare" ? null : trip.preference;
      const preferredModes = compareModeKey
        ? sortedAvailableModes.filter((mode) => mode.key === compareModeKey)
        : sortedAvailableModes;
      const recommendedMode =
        preferredModes[0] ||
        sortedAvailableModes[0] ||
        null;

      acc[key] = {
        entryPrice,
        entryTotal,
        day: parkData[key].day,
        distanceMiles: miles,
        transportModes,
        recommendedMode,
        cheapestMode: sortedAvailableModes[0] || null,
        bestOverallHeadline: recommendedMode?.total != null ? money(recommendedMode.total) : "—",
      };
      return acc;
    }, {});
  }, [distance, family.adults, family.kidsTall, family.payingPassengers, foodEstimate.max, foodEstimate.mid, foodEstimate.min, liveTransport, parkData, trip.preference]);

  const overallRecommendation = useMemo(() => {
    const openParks = PARK_KEYS.filter((key) => parkModels[key]?.day?.is_open);
    if (!openParks.length) {
      return {
        title: "Both parks look closed for this date.",
        body: "Try another date to compare live tickets, rail and taxi.",
        chips: ["Check seasonal calendars"],
      };
    }

    const preferredMode = trip.preference === "compare" ? null : trip.preference;
    const candidates = openParks
      .map((key) => {
        const park = parkModels[key];
        const mode = preferredMode ? park.transportModes[preferredMode] : park.recommendedMode;
        if (!mode || mode.total == null) return null;
        return { key, park, mode };
      })
      .filter(Boolean)
      .sort((a, b) => a.mode.total - b.mode.total);

    if (!candidates.length) {
      return {
        title: "Waiting for enough live data to compare.",
        body: "Ticket or transport totals are still loading for your postcode and date.",
        chips: [],
      };
    }

    const winner = candidates[0];
    const runnerUp = candidates[1] || null;
    const saving = runnerUp ? Math.round(runnerUp.mode.total - winner.mode.total) : null;
    const modeLabel = winner.mode.label.toLowerCase();
    const openBits = openParks.map((key) => {
      const day = parkModels[key].day;
      return `${PARKS[key].shortName} ${formatTime(day?.open_time)}-${formatTime(day?.close_time)}`;
    });

    return {
      title: `${winner.park.day?.is_open ? "Best match for this trip" : "Current recommendation"}: ${PARKS[winner.key].name} for ${modeLabel === "self drive" ? "driving" : modeLabel}.`,
      body: saving != null && saving > 0
        ? `${PARKS[winner.key].shortName} is about £${saving} cheaper than the other open park for your selected mode and family size.`
        : `${PARKS[winner.key].shortName} is the strongest pick on this date for your current filters.`,
      chips: [
        preferredMode ? `Focused on ${winner.mode.label.toLowerCase()}` : `Cheapest option: ${winner.mode.label}`,
        `${winner.mode.duration || "—"} mins`,
        ...openBits,
      ],
    };
  }, [parkModels, trip.preference]);

  const comparisonRows = useMemo(() => {
    return [
      {
        label: "Open on your date",
        legoland: parkModels.legoland.day?.is_open ? `${formatTime(parkModels.legoland.day.open_time)}-${formatTime(parkModels.legoland.day.close_time)}` : "Closed",
        thorpe: parkModels.thorpe.day?.is_open ? `${formatTime(parkModels.thorpe.day.open_time)}-${formatTime(parkModels.thorpe.day.close_time)}` : "Closed",
      },
      {
        label: "Ticket from-price",
        legoland: parkModels.legoland.entryPrice != null ? money(parkModels.legoland.entryPrice) : "No data",
        thorpe: parkModels.thorpe.entryPrice != null ? money(parkModels.thorpe.entryPrice) : "No data",
      },
      {
        label: "Cheapest full-day option",
        legoland: parkModels.legoland.cheapestMode ? `${parkModels.legoland.cheapestMode.label} ${money(parkModels.legoland.cheapestMode.total)}` : "—",
        thorpe: parkModels.thorpe.cheapestMode ? `${parkModels.thorpe.cheapestMode.label} ${money(parkModels.thorpe.cheapestMode.total)}` : "—",
      },
      {
        label: "Drive total",
        legoland: parkModels.legoland.transportModes.drive ? rangeText(parkModels.legoland.transportModes.drive.rangeMin, parkModels.legoland.transportModes.drive.rangeMax) : "—",
        thorpe: parkModels.thorpe.transportModes.drive ? rangeText(parkModels.thorpe.transportModes.drive.rangeMin, parkModels.thorpe.transportModes.drive.rangeMax) : "—",
      },
      {
        label: "Train total",
        legoland: parkModels.legoland.transportModes.public ? `${rangeText(parkModels.legoland.transportModes.public.rangeMin, parkModels.legoland.transportModes.public.rangeMax)}${parkModels.legoland.transportModes.public.liveBadge ? ` (${parkModels.legoland.transportModes.public.liveBadge})` : ""}` : "—",
        thorpe: parkModels.thorpe.transportModes.public ? `${rangeText(parkModels.thorpe.transportModes.public.rangeMin, parkModels.thorpe.transportModes.public.rangeMax)}${parkModels.thorpe.transportModes.public.liveBadge ? ` (${parkModels.thorpe.transportModes.public.liveBadge})` : ""}` : "—",
      },
      {
        label: "Taxi total",
        legoland: parkModels.legoland.transportModes.taxi ? `${rangeText(parkModels.legoland.transportModes.taxi.rangeMin, parkModels.legoland.transportModes.taxi.rangeMax)}${parkModels.legoland.transportModes.taxi.liveBadge ? ` (${parkModels.legoland.transportModes.taxi.liveBadge})` : ""}` : "—",
        thorpe: parkModels.thorpe.transportModes.taxi ? `${rangeText(parkModels.thorpe.transportModes.taxi.rangeMin, parkModels.thorpe.transportModes.taxi.rangeMax)}${parkModels.thorpe.transportModes.taxi.liveBadge ? ` (${parkModels.thorpe.transportModes.taxi.liveBadge})` : ""}` : "—",
      },
      {
        label: "Best for",
        legoland: PARKS.legoland.bestFor,
        thorpe: PARKS.thorpe.bestFor,
      },
      {
        label: "Under-height kids",
        legoland: "Under 90cm enter free",
        thorpe: "Under 1.2m enter free, but many rides restrict height",
      },
      {
        label: "Train last mile",
        legoland: "Usually Slough + short taxi",
        thorpe: "Usually Staines + express bus",
      },
      {
        label: "Parking walk",
        legoland: `${PARKS.legoland.walkMins} mins from standard parking`,
        thorpe: `${PARKS.thorpe.walkMins} mins from standard parking`,
      },
    ];
  }, [parkModels]);

  const parkBadges = useMemo(() => {
    return PARK_KEYS.reduce((acc, parkKey) => {
      const modes = Object.values(parkModels[parkKey].transportModes).filter(Boolean);
      if (!modes.length) {
        acc[parkKey] = null;
        return acc;
      }

      const cheapest = [...modes].sort(
        (a, b) => (a.total ?? Number.POSITIVE_INFINITY) - (b.total ?? Number.POSITIVE_INFINITY)
      )[0];
      const fastest = [...modes].sort(
        (a, b) => (a.duration ?? Number.POSITIVE_INFINITY) - (b.duration ?? Number.POSITIVE_INFINITY)
      )[0];

      let label = "Best value";
      if (parkModels[parkKey].day?.is_open === false) {
        label = "Closed on this date";
      } else if (trip.preference === "taxi") {
        label = "Best without a car";
      } else if (trip.preference === "public") {
        label = "Best by train";
      } else if (trip.preference === "drive") {
        label = "Best by car";
      } else if (fastest?.key === "taxi") {
        label = "Fastest";
      } else if (cheapest?.key === "drive") {
        label = "Cheapest: Drive";
      } else if (cheapest?.key === "public") {
        label = "Cheapest: Train";
      } else if (cheapest?.key === "taxi") {
        label = "Cheapest: Taxi";
      }

      acc[parkKey] = label;
      return acc;
    }, {});
  }, [parkModels, trip.preference]);

  const renderModeCard = (parkKey, modeKey, mode) => {
    const parkIsClosed = parkModels[parkKey].day?.is_open === false;
    if (!mode) {
      return (
        <div
          key={modeKey}
          style={{
            border: "1px solid #dbe4f0",
            borderRadius: 18,
            padding: isMobile ? 10 : 14,
            background: "#ffffff",
            minHeight: 255,
          }}
        >
          <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 800 }}>{modeKey === "drive" ? "Self drive" : modeKey === "public" ? "Train" : "Taxi"}</div>
          <div style={{ marginTop: 10, fontSize: isMobile ? 12 : 13, color: "#64748b" }}>Waiting for enough data to price this option.</div>
        </div>
      );
    }

    if (parkIsClosed) {
      return (
        <div
          key={modeKey}
          style={{
            border: "1.5px solid #dbe4f0",
            borderRadius: 18,
            padding: isMobile ? 10 : 16,
            background: "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)",
            boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
            minHeight: isMobile ? "auto" : 255,
            opacity: 0.9,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontSize: isMobile ? 16 : 24, fontWeight: 900, color: "#334155" }}>{mode.label}</div>
            {mode.liveBadge ? (
              <div
                style={{
                  marginLeft: "auto",
                  padding: isMobile ? "4px 8px" : "5px 9px",
                  borderRadius: 999,
                  fontSize: isMobile ? 10 : 11,
                  fontWeight: 800,
                  background: "#f0fdfa",
                  border: "1px solid #99f6e4",
                  color: "#0f766e",
                  lineHeight: 1,
                }}
              >
                {mode.liveBadge}
              </div>
            ) : null}
          </div>
        <div style={{ marginTop: 10, fontSize: isMobile ? 10 : 11, fontWeight: 800, letterSpacing: 0.7, color: "#64748b", textTransform: "uppercase" }}>
          Unavailable on this date
        </div>
        <div style={{ marginTop: 5, fontSize: isMobile ? 18 : 28, fontWeight: 900, lineHeight: 1.05, color: "#475467" }}>
          Not available
        </div>
        <div style={{ marginTop: 6, fontSize: isMobile ? 12 : 14, color: "#64748b", lineHeight: 1.35 }}>
          Pick another open date to compare this mode.
        </div>
          <div style={{ marginTop: 12, fontSize: isMobile ? 12 : 14, color: "#334155" }}>{mode.duration ? `${mode.duration} mins` : "Time pending"}</div>
          <div style={{ marginTop: 8, fontSize: isMobile ? 12 : 14, color: "#334155" }}>{mode.walkChangesLabel || `${mode.walkMins ?? 0} min walk`}</div>
          <div style={{ marginTop: 8, fontSize: isMobile ? 11.5 : 14, color: "#334155", lineHeight: 1.35 }}>{mode.additionalInfo || mode.meta}</div>
        <details style={{ marginTop: 8 }}>
          <summary style={{ cursor: "pointer", fontSize: isMobile ? 11 : 12, fontWeight: 700, color: "#64748b" }}>
            Cost details
          </summary>
          <div style={{ marginTop: 6, fontSize: isMobile ? 12 : 13, lineHeight: 1.4, color: "#64748b" }}>{mode.breakdown}</div>
          </details>
        </div>
      );
    }

    const recommended = parkModels[parkKey].recommendedMode?.key === mode.key;
    const palette = {
      drive: {
        border: recommended ? "#f59e0b" : "#e5e7eb",
        background: recommended ? "linear-gradient(180deg, #fff8e6 0%, #fff3cf 100%)" : "#ffffff",
        shadow: recommended ? "0 14px 30px rgba(245, 158, 11, 0.18)" : "0 10px 24px rgba(15, 23, 42, 0.08)",
      },
      public: {
        border: recommended ? "#2563eb" : "#e5e7eb",
        background: recommended ? "linear-gradient(180deg, #eef5ff 0%, #f7fbff 100%)" : "#ffffff",
        shadow: recommended ? "0 14px 30px rgba(37, 99, 235, 0.16)" : "0 10px 24px rgba(15, 23, 42, 0.08)",
      },
      taxi: {
        border: recommended ? "#10b981" : "#e5e7eb",
        background: recommended ? "linear-gradient(180deg, #ecfdf5 0%, #f7fffb 100%)" : "#ffffff",
        shadow: recommended ? "0 14px 30px rgba(16, 185, 129, 0.16)" : "0 10px 24px rgba(15, 23, 42, 0.08)",
      },
    };

    return (
      <div
        key={modeKey}
        style={{
          border: `2px solid ${palette[mode.key].border}`,
          borderRadius: 18,
          padding: isMobile ? 10 : 16,
          background: parkIsClosed ? "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)" : palette[mode.key].background,
          boxShadow: palette[mode.key].shadow,
          minHeight: isMobile ? "auto" : 255,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ fontSize: isMobile ? 16 : 24, fontWeight: 900, color: "#0f172a" }}>{mode.label}</div>
          {mode.liveBadge ? (
            <div
              style={{
                marginLeft: "auto",
                padding: isMobile ? "4px 8px" : "5px 9px",
                borderRadius: 999,
                fontSize: isMobile ? 10 : 11,
                fontWeight: 800,
                background: "#ecfeff",
                border: "1px solid #a5f3fc",
                color: "#0f766e",
                lineHeight: 1,
              }}
            >
              {mode.liveBadge}
            </div>
          ) : null}
        </div>
        <div style={{ marginTop: 10, fontSize: isMobile ? 10 : 11, fontWeight: 800, letterSpacing: 0.7, color: "#64748b", textTransform: "uppercase" }}>
          Total day cost ({family.adults} adults{family.kidsTall ? ` + ${family.kidsTall} child` : ""})
        </div>
        <div style={{ marginTop: 4, fontSize: isMobile ? 24 : 36, fontWeight: 900, lineHeight: 1, color: "#0f172a" }}>
          {mode.total != null ? money(mode.total) : "—"}
        </div>
        <div style={{ marginTop: 10, fontSize: isMobile ? 12 : 14, color: "#334155" }}>{mode.fareLabel || money(mode.transportCost)}</div>
        <div style={{ marginTop: 8, fontSize: isMobile ? 12 : 14, color: "#334155" }}>{mode.duration ? `${mode.duration} mins` : "Time pending"}</div>
        <div style={{ marginTop: 8, fontSize: isMobile ? 12 : 14, color: "#334155" }}>{mode.walkChangesLabel || `${mode.walkMins ?? 0} min walk`}</div>
        <div style={{ marginTop: 8, fontSize: isMobile ? 12 : 14, color: "#334155" }}>{mode.hassleLabel || "Medium hassle"}</div>
        <details style={{ marginTop: 8 }}>
          <summary style={{ cursor: "pointer", fontSize: isMobile ? 11 : 12, fontWeight: 700, color: "#475467" }}>
            Cost details
          </summary>
          <div style={{ marginTop: 6, fontSize: isMobile ? 12 : 13, lineHeight: 1.4, color: "#475467" }}>{mode.breakdown}</div>
        </details>
        <div style={{ marginTop: 4, fontSize: isMobile ? 11.5 : 13, lineHeight: 1.35, color: "#475467" }}>{mode.additionalInfo || mode.meta}</div>
        {recommended ? (
          <div style={{ marginTop: 10, fontSize: isMobile ? 11.5 : 13, fontWeight: 800, color: "#0f766e" }}>
            Recommended for this park
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="parking-wrapper">
      <Helmet>
        <title>LEGOLAND vs Thorpe Park 2026 - Live Train, Taxi and Drive Comparison</title>
        <meta
          name="description"
          content="Compare LEGOLAND Windsor and Thorpe Park in 2026 using your postcode and date: live tickets, live rail, live taxi, drive totals, and side-by-side timings for your family."
        />
        <link rel="canonical" href="https://www.cheapcabgrab.com/legoland-vs-thorpe-park" />
        <meta property="og:title" content="LEGOLAND vs Thorpe Park - Live Travel and Budget Comparison" />
        <meta
          property="og:description"
          content="Two-park 2026 live comparison with postcode-aware tickets, rail, taxi, drive costs, and side-by-side timings."
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "LEGOLAND vs Thorpe Park Comparison",
            applicationCategory: "TravelApplication",
            operatingSystem: "Web",
            url: "https://www.cheapcabgrab.com/legoland-vs-thorpe-park",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "GBP"
            },
            featureList: [
              "Compares LEGOLAND Windsor and Thorpe Park side by side",
              "Includes live taxi fares and live National Rail fares where available",
              "Includes drive totals, ticket prices, food ranges, and family fit notes",
              "Includes side-by-side timings for each travel mode",
              "Uses postcode-aware travel costs for your selected date in 2026"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "Which is cheaper, LEGOLAND or Thorpe Park?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Use the park cards above. The comparison page checks drive, train and taxi separately for the same family size, postcode and date."
                }
              },
              {
                "@type": "Question",
                name: "Are the train and taxi prices live?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes. The comparison uses the live rail and taxi backends where available, with fallback labelling when a live result is unavailable."
                }
              },
              {
                "@type": "Question",
                name: "Which park is easier without a car?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Thorpe Park often has the simpler rail finish because the final bus from Staines is explicit in the live plan, while LEGOLAND usually needs a last-mile taxi from Slough or Windsor."
                }
              },
              {
                "@type": "Question",
                name: "Which park is better for younger kids?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "LEGOLAND usually suits younger children better, especially if you have under-90cm riders, while Thorpe Park is stronger for older kids and thrill seekers."
                }
              },
              {
                "@type": "Question",
                name: "What does this live comparison include?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "It includes live taxi fares where available, live National Rail fares where available, drive totals, opening hours, ticket prices, family fit notes, side-by-side mode timings, walking time, changes, hassle signals, and opening-time weather when available."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <LiveStrip variant="legoland" />

      <div
        className="parking-header"
        style={{
          borderBottom: "3px solid #0ea5e9",
          background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
          borderRadius: 20,
          padding: isMobile ? "12px 14px 14px" : undefined,
          paddingBottom: isMobile ? 14 : 18,
          marginTop: isMobile ? 6 : undefined,
        }}
      >
        <h1 style={isMobile ? { fontSize: 34, lineHeight: 1.06, marginBottom: 10 } : undefined}>
          LEGOLAND or Thorpe Park? Live cost and travel comparison
        </h1>
        <p style={isMobile ? { fontSize: 17, lineHeight: 1.4, marginBottom: 8 } : undefined}>
          Compare both parks side by side with live rail, taxi and drive totals for your family.
        </p>
        <div style={{ fontSize: isMobile ? 10 : 11, color: "#94a3b8", fontWeight: 500 }}>Updated: 19th March 2026</div>
      </div>

      {refreshFlash && (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 10px",
            marginTop: 8,
            borderRadius: 999,
            background: "#e0f2fe",
            border: "1px solid #bae6fd",
            color: "#0f172a",
            fontSize: 12,
            boxShadow: "0 6px 14px rgba(0,0,0,0.06)",
          }}
        >
          <span>Updating comparison...</span>
        </div>
      )}

      <div
        className="parking-form"
        style={{
          borderRadius: 22,
          border: "1px solid #e2e8f0",
          background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
          boxShadow: "0 18px 36px rgba(15, 23, 42, 0.06)",
        }}
      >
        <div className="option-grid" style={{ gap: 10 }}>
          <div className="form-group" style={{ minWidth: 0 }}>
            <label style={labelStyle}>Pickup postcode</label>
            <div style={{ position: "relative" }}>
              <input
                className="parking-input"
                value={formTrip.pickup}
                onChange={(e) => {
                  triggerUpdateFlash("pickup");
                  setFormTrip({ ...formTrip, pickup: e.target.value });
                  setShowPostcodeSuggestions(String(e.target.value || "").replace(/\s+/g, "").length >= 3);
                }}
                onFocus={() => {
                  if (visiblePostcodeSuggestions.length) setShowPostcodeSuggestions(true);
                }}
                onBlur={() => {
                  setTimeout(() => setShowPostcodeSuggestions(false), 120);
                }}
                placeholder="e.g. SW1A 1AA"
                autoComplete="postal-code"
                style={mobileCompactInput}
              />
              {showPostcodeSuggestions && visiblePostcodeSuggestions.length > 0 && (
                <div
                  style={{
                    position: "absolute",
                    top: "calc(100% + 6px)",
                    left: 0,
                    right: 0,
                    zIndex: 20,
                    background: "#ffffff",
                    border: "1px solid #dbe4f0",
                    borderRadius: 14,
                    boxShadow: "0 16px 30px rgba(15,23,42,0.12)",
                    overflow: "hidden",
                  }}
                >
                  {visiblePostcodeSuggestions.slice(0, 6).map((postcode, idx) => (
                    <button
                      key={postcode}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handlePostcodeSuggestionPick(postcode)}
                      style={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        padding: "10px 12px",
                        background: "#ffffff",
                        border: "none",
                        borderTop: idx === 0 ? "none" : "1px solid #eef2f7",
                        fontSize: isMobile ? 15 : 14,
                        color: "#0f172a",
                      }}
                    >
                      {postcode}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4, lineHeight: 1.35 }}>
              {postcodeLookupLoading ? "Checking postcode..." : validatedPickup ? "Valid postcode" : "Enter a full UK postcode"}
              {!postcodeLookupLoading && visiblePostcodeSuggestions.length > 0 ? ` • ${visiblePostcodeSuggestions.length} suggestions` : ""}
            </div>
            {postcodeError ? <div style={{ color: "#b45309", fontSize: 12, marginTop: 4 }}>{postcodeError}</div> : null}
            {!postcodeError && distance.error ? <div style={{ color: "#b45309", fontSize: 12, marginTop: 4 }}>{distance.error}</div> : null}
          </div>
          <div className="form-group" style={{ minWidth: 0 }}>
            <label style={labelStyle}>Date of visit</label>
            <input
              type="date"
              className="parking-input"
              value={toDateOnly(formTrip.date)}
              onChange={(e) => {
                triggerUpdateFlash("date");
                setFormTrip({ ...formTrip, date: e.target.value });
              }}
              style={mobileCompactInput}
            />
          </div>
        </div>

        <div className="option-grid compact-two" style={{ gap: 10 }}>
          <div className="form-group" style={{ minWidth: 0 }}>
            <label style={labelStyle}>Adults</label>
            <input
              type="number"
              min="0"
              max="8"
              className="parking-input"
              value={formTrip.adults}
              onChange={(e) => {
                triggerUpdateFlash("adults");
                setFormTrip({ ...formTrip, adults: e.target.value });
              }}
              style={mobileCompactInput}
            />
          </div>
          <div className="form-group" style={{ minWidth: 0 }}>
            <label style={labelStyle}>Children (paying)</label>
            <input
              type="number"
              min="0"
              max="8"
              className="parking-input"
              value={formTrip.kidsTall}
              onChange={(e) => {
                triggerUpdateFlash("kidsTall");
                setFormTrip({ ...formTrip, kidsTall: e.target.value });
              }}
              style={mobileCompactInput}
            />
          </div>
          <div className="form-group" style={{ minWidth: 0 }}>
            <label style={labelStyle}>Children ({PARKS.legoland.freeHeightLabel} / {PARKS.thorpe.freeHeightLabel})</label>
            <input
              type="number"
              min="0"
              max="8"
              className="parking-input"
              value={formTrip.kidsSmall}
              onChange={(e) => {
                triggerUpdateFlash("kidsSmall");
                setFormTrip({ ...formTrip, kidsSmall: e.target.value });
              }}
              style={mobileCompactInput}
            />
          </div>
          <div className="form-group" style={{ minWidth: 0 }}>
            <label style={labelStyle}>Focus the comparison</label>
            <select
              className="parking-input"
              value={formTrip.preference}
              onChange={(e) => {
                triggerUpdateFlash("preference");
                setFormTrip({ ...formTrip, preference: e.target.value });
              }}
              style={mobileCompactSelect}
            >
              <option value="compare">Best overall</option>
              <option value="drive">Drive</option>
              <option value="public">Train</option>
              <option value="taxi">Taxi</option>
            </select>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 10 : 12, flexWrap: "wrap", marginTop: isMobile ? 10 : 14 }}>
          <button
            type="button"
            className="parking-btn"
            onClick={applyComparisonInputs}
            disabled={compareDisabled}
            style={{
              width: "auto",
              minWidth: isMobile ? 0 : 180,
              padding: isMobile ? "8px 12px" : undefined,
              fontSize: isMobile ? 14 : undefined,
              background: compareDisabled ? "#e5e7eb" : hasPendingChanges ? undefined : "#dbeafe",
              color: compareDisabled ? "#475467" : hasPendingChanges ? undefined : "#1d4ed8",
              boxShadow: compareDisabled ? "none" : hasPendingChanges ? undefined : "0 8px 22px rgba(59,130,246,0.18)",
              borderRadius: isMobile ? 12 : undefined,
              cursor: compareDisabled ? "default" : "pointer",
            }}
          >
            {postcodeLookupLoading ? "Checking postcode..." : hasPendingChanges ? "Compare now" : "Refresh comparison"}
          </button>
          {hasPendingChanges && !postcodeLookupLoading && (
            <div style={{ fontSize: isMobile ? 12 : 13, color: "#475467", lineHeight: 1.35 }}>
              Inputs changed. Apply to refresh the live comparison below.
            </div>
          )}
        </div>
      </div>

      <div
        style={{
          marginTop: 16,
          borderRadius: 24,
          padding: isMobile ? 11 : 18,
          background: "linear-gradient(135deg, #0f5bd3 0%, #09a4df 45%, #21c55d 100%)",
          color: "#ffffff",
          boxShadow: "0 20px 36px rgba(14, 165, 233, 0.22)",
        }}
      >
        <div style={{ fontSize: isMobile ? 10 : 12, fontWeight: 800, letterSpacing: isMobile ? 0.45 : 0.8, textTransform: "uppercase", opacity: 0.9 }}>
          Live comparison for {selectedDate ? formatDisplayDate(selectedDate) : "your date"} - {family.size} travellers from {trip.pickup || "your postcode"}
        </div>
        <div style={{ marginTop: 5, fontSize: isMobile ? 19 : 34, lineHeight: isMobile ? 1.08 : 1.08, fontWeight: 900 }}>
          {overallRecommendation.title}
        </div>
        <div style={{ marginTop: 4, fontSize: isMobile ? 12 : 16, maxWidth: 900, lineHeight: isMobile ? 1.28 : 1.35 }}>
          {overallRecommendation.body}
        </div>
        {overallRecommendation.chips?.length ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: isMobile ? 6 : 8, marginTop: 8 }}>
            {overallRecommendation.chips.map((chip) => (
              <div
                key={chip}
                style={{
                  padding: isMobile ? "5px 7px" : "7px 10px",
                  borderRadius: 999,
                  background: "rgba(255,255,255,0.16)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  fontSize: isMobile ? 10 : 12,
                  fontWeight: 700,
                }}
              >
                {chip}
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {(liveTransport.error || distance.loading || liveTransport.loading) && (
        <div className="parking-disclaimer" style={{ marginTop: 10 }}>
          {liveTransport.loading || distance.loading
            ? "Refreshing live rail, taxi and park pricing..."
            : liveTransport.error}
        </div>
      )}

      <div
        style={{
          marginTop: 18,
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))",
          gap: 16,
        }}
      >
        {PARK_KEYS.map((parkKey) => {
          const park = PARKS[parkKey];
          const model = parkModels[parkKey];
          const day = model.day;
          const open = Boolean(day?.is_open);
          return (
            <section
              key={parkKey}
              style={{
                borderRadius: 26,
                border: `1px solid ${park.accent.border}33`,
                background: park.accent.soft,
                boxShadow: "0 18px 38px rgba(15, 23, 42, 0.08)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: isMobile ? 16 : 20,
                  background: park.accent.hero,
                  color: "#ffffff",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.8, opacity: 0.85 }}>
                      {open ? "Park status" : "Status check"}
                    </div>
                    <div style={{ marginTop: 6, fontSize: isMobile ? 28 : 34, lineHeight: 1.05, fontWeight: 900 }}>
                      {park.name}
                    </div>
                  </div>
                  <div
                    style={{
                      padding: "7px 10px",
                      borderRadius: 999,
                      background: open ? "rgba(255,255,255,0.16)" : "rgba(255,255,255,0.12)",
                      border: "1px solid rgba(255,255,255,0.2)",
                      fontSize: 12,
                      fontWeight: 800,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {open ? "Open" : "Closed"}
                  </div>
                </div>
                <div style={{ marginTop: 12, fontSize: 18, fontWeight: 800 }}>
                  {open
                    ? `${park.shortName} is open on ${selectedDate ? formatDisplayDate(selectedDate) : "your date"}`
                    : `${park.shortName} looks closed on ${selectedDate ? formatDisplayDate(selectedDate) : "this date"}`}
                </div>
                <div style={{ marginTop: 6, fontSize: 16, lineHeight: 1.35, opacity: 0.96 }}>
                  {open
                    ? `Open ${formatTime(day?.open_time)}-${formatTime(day?.close_time)}${model.entryPrice != null ? ` • From ${money(model.entryPrice)} per ticket` : ""}`
                    : parkData[parkKey].dayError || "Try another date to compare live totals."}
                </div>
                {parkWeather[parkKey] ? (
                  <div style={{ marginTop: 6, fontSize: isMobile ? 10.5 : 11.5, lineHeight: 1.32, opacity: 0.84 }}>
                    {parkWeather[parkKey]}
                  </div>
                ) : null}
                <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 8 }}>
                  <div
                    style={{
                      padding: "6px 9px",
                      borderRadius: 999,
                      background: "rgba(255,255,255,0.14)",
                      border: "1px solid rgba(255,255,255,0.18)",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    Best for: {park.bestFor}
                  </div>
                  <div
                    style={{
                      padding: "6px 9px",
                      borderRadius: 999,
                      background: "rgba(255,255,255,0.14)",
                      border: "1px solid rgba(255,255,255,0.18)",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    {park.freeHeightLabel} free
                  </div>
                  <div
                    style={{
                      padding: "6px 9px",
                      borderRadius: 999,
                      background: "rgba(255,255,255,0.14)",
                      border: "1px solid rgba(255,255,255,0.18)",
                      fontSize: 12,
                      fontWeight: 800,
                    }}
                  >
                    {parkBadges[parkKey] || "Best value"}
                  </div>
                </div>
              </div>

              <div style={{ padding: isMobile ? 14 : 18 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                  <div
                    style={{
                      padding: "6px 10px",
                      borderRadius: 999,
                      background: park.accent.chip,
                      color: park.accent.chipText,
                      fontSize: 12,
                      fontWeight: 800,
                    }}
                  >
                    Recommended: {model.recommendedMode?.label || "Pending"}
                  </div>
                  <div style={{ fontSize: 13, color: "#475467" }}>
                    {model.recommendedMode?.total != null ? `${money(model.recommendedMode.total)} headline total` : "Waiting for full totals"}
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))",
                    gap: 12,
                    alignItems: "stretch",
                  }}
                >
                  {renderModeCard(parkKey, "drive", model.transportModes.drive)}
                  {renderModeCard(parkKey, "public", model.transportModes.public)}
                  {renderModeCard(parkKey, "taxi", model.transportModes.taxi)}
                </div>
              </div>
            </section>
          );
        })}
      </div>

      <section
        style={{
          marginTop: 18,
          borderRadius: 22,
          border: "1px solid #dbeafe",
          background: "linear-gradient(180deg, #f8fbff 0%, #f3f8ff 100%)",
          boxShadow: "0 16px 32px rgba(15, 23, 42, 0.06)",
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "14px 16px 0", fontSize: 14, fontWeight: 900, color: "#0284c7" }}>
          At-a-glance comparison
        </div>
        <div className="responsive-table" style={{ padding: 16 }}>
          <table className="parking-table">
            <thead>
              <tr>
                <th style={{ width: "30%" }}></th>
                <th>{PARKS.legoland.name}</th>
                <th>{PARKS.thorpe.name}</th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row) => (
                <tr key={row.label} style={{ background: ["Cheapest full-day option", "Train total", "Taxi total"].includes(row.label) ? "#ffffff" : "transparent" }}>
                  <td style={{ fontWeight: 800 }}>{row.label}</td>
                  <td>{row.legoland}</td>
                  <td>{row.thorpe}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="parking-disclaimer" style={{ padding: "0 16px 16px" }}>
          Taxi and train use the live comparison backends when available. Drive remains modelled from postcode distance, fuel and parking.
        </div>
      </section>

      <details className="train-collapsible" style={{ marginTop: 18 }}>
        <summary className="train-summary-bar">✅ What this live comparison includes</summary>
        <div className="train-collapsible-body">
          <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.55 }}>
            <li>Live taxi pricing where CheapCabGrab preview fares are available for your search.</li>
            <li>Live National Rail fares where the rail planner returns a live route.</li>
            <li>Real parking prices, opening days, opening hours, and current ticket from-prices for both parks.</li>
            <li>Family-aware ticket handling, including under-90cm LEGOLAND riders and under-1.2m Thorpe riders.</li>
            <li>Side-by-side leave-home timings for drive, taxi, and train where the planners can build a full route shape.</li>
            <li>Rail context showing first mile to the station, then the train, then the last-mile finish.</li>
            <li>Thorpe rail usually finishes via Staines and the 950 bus, with 446 via Chertsey as a fallback route shape.</li>
            <li>LEGOLAND rail usually finishes via Slough or Windsor, with taxi preferred for the last mile when that gives the clearest family comparison.</li>
            <li>Walking time, changes, and hassle guidance so the cheapest option is not the only signal.</li>
            <li>Opening-time weather summaries for each park when forecast data is available.</li>
          </ul>
          <div className="parking-disclaimer" style={{ marginTop: 8 }}>
            Drive remains modelled from postcode distance, fuel, parking, and timing assumptions. Rail and taxi use the live comparison backends when those responses are available.
          </div>
        </div>
      </details>

      <section
        style={{
          marginTop: 18,
          borderRadius: 22,
          border: "1px solid #fef3c7",
          background: "linear-gradient(180deg, #fffdf4 0%, #fff8db 100%)",
          boxShadow: "0 16px 32px rgba(15, 23, 42, 0.05)",
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "14px 16px 0", fontSize: 14, fontWeight: 900, color: "#b45309" }}>
          Ride fit and family notes
        </div>
        <div className="responsive-table" style={{ padding: 16 }}>
          <table className="parking-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>LEGOLAND</th>
                <th>Thorpe Park</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Best ages</td>
                <td>2-10</td>
                <td>10+</td>
              </tr>
              <tr>
                <td>Free entry height</td>
                <td>Under 90cm</td>
                <td>Under 1.2m</td>
              </tr>
              <tr>
                <td>Ride intensity</td>
                <td>{PARKS.legoland.thrill}</td>
                <td>{PARKS.thorpe.thrill}</td>
              </tr>
              <tr>
                <td>Walking from standard parking</td>
                <td>{PARKS.legoland.walkMins} mins</td>
                <td>{PARKS.thorpe.walkMins} mins</td>
              </tr>
              <tr>
                <td>Train last mile</td>
                <td>Slough or Windsor area connection, usually short taxi finish</td>
                <td>Staines plus the 950 express bus</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <details className="train-collapsible" style={{ marginTop: 16 }}>
        <summary className="train-summary-bar">FAQs</summary>
        <div className="train-collapsible-body">
          <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
            <li><strong>Which is cheaper?</strong> Use the park cards above. They now compare drive, train and taxi separately for the same family and date.</li>
            <li><strong>Are the train and taxi prices live?</strong> Yes, the comparison uses the new live rail and taxi backends where available, with fallback labelling if live rail fails.</li>
            <li><strong>Why is drive not marked live?</strong> Drive is still a postcode-based estimate built from distance, fuel and parking rather than a live quote.</li>
            <li><strong>Which park is easier without a car?</strong> Thorpe usually has the simpler rail finish because the express bus from Staines is explicit in the live plan.</li>
            <li><strong>Which park is better for younger kids?</strong> LEGOLAND usually suits younger children better, especially if you have under-90cm riders.</li>
          </ul>
        </div>
      </details>

      <div style={{ marginTop: 16, padding: 12, border: "1px solid #e5e7eb", borderRadius: 10, background: "#f9fafb" }}>
        <strong>Plan next:</strong>
        <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
          <li><a href="/legoland-budget-planner">LEGOLAND Budget Planner</a></li>
          <li><a href="/thorpepark-budget-planner">Thorpe Park Budget Planner</a></li>
          <li><a href="/east-midlands-airport/live-journey-planner">East Midlands Airport live journey monitor</a></li>
        </ul>
      </div>

      <div style={{ marginTop: 24 }}>
        <ReviewsBar />
      </div>
    </div>
  );
}
